import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { clampBlockDimensionMm } from '../block-configurator/dimension-ops'
import { formatMm } from './format-mm'

interface ZoneDimensionsFieldProps {
  widthMm?: number
  heightMm?: number
  disabled?: boolean
  onChange?: (next: { widthMm: number; heightMm: number }) => void
}

interface DimensionCommitInputProps {
  testId: string
  label: string
  valueMm: number
  onCommit: (next: number) => void
}

/**
 * One width/height number input: keeps a local draft while typing and commits
 * to the parent on blur or Enter — NOT on every keystroke. Same pattern as
 * CommitInput / RulerDimensionInput / FontSizeInput / BarcodeSizeField, so
 * each edit becomes one undo step (issue #132). step=0.5 + parseFloat for
 * 0.5mm granularity.
 */
function DimensionCommitInput({ testId, label, valueMm, onCommit }: DimensionCommitInputProps) {
  const [draft, setDraft] = useState(formatMm(valueMm))
  // Track whether we already committed on Enter so blur doesn't double-fire.
  const committedRef = useRef(false)

  useEffect(() => {
    setDraft(formatMm(valueMm))
    committedRef.current = false
  }, [valueMm])

  const commit = (raw: string) => {
    if (committedRef.current) return
    const parsed = parseFloat(raw)
    if (Number.isNaN(parsed)) return
    committedRef.current = true
    onCommit(clampBlockDimensionMm(parsed))
  }

  return (
    <input
      type="number"
      min={1}
      max={300}
      step={0.5}
      aria-label={label}
      data-testid={testId}
      value={draft}
      onChange={e => {
        committedRef.current = false
        setDraft(e.target.value)
      }}
      onBlur={() => commit(draft)}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          commit(draft)
          e.currentTarget.blur()
        }
      }}
      className="w-10 rounded border border-stone-300 px-1 py-0.5 text-center font-mono text-[10px] dark:border-stone-700 dark:bg-stone-900 dark:text-stone-100"
    />
  )
}

export function ZoneDimensionsField({ widthMm, heightMm, disabled, onChange }: ZoneDimensionsFieldProps) {
  // Hook before the early return — hook order must not depend on the props.
  const { t } = useTranslation()
  if (widthMm === undefined || heightMm === undefined) return null

  if (!onChange || disabled) {
    return (
      <span
        data-testid="zone-dimensions-field"
        className="ml-2 rounded border border-stone-300 px-1.5 py-0.5 font-mono text-[10px] text-stone-600 dark:border-stone-700 dark:text-stone-300"
      >
        {formatMm(widthMm)}×{formatMm(heightMm)} mm
      </span>
    )
  }

  return (
    <span
      data-testid="zone-dimensions-field"
      className="ml-2 inline-flex items-center gap-0.5 text-[10px]"
    >
      <DimensionCommitInput
        testId="zone-dimensions-width"
        label={t('zoneSize.width')}
        valueMm={widthMm}
        onCommit={next => onChange({ widthMm: next, heightMm })}
      />
      <span className="text-stone-500">×</span>
      <DimensionCommitInput
        testId="zone-dimensions-height"
        label={t('zoneSize.height')}
        valueMm={heightMm}
        onCommit={next => onChange({ widthMm, heightMm: next })}
      />
      <span className="text-stone-500">mm</span>
    </span>
  )
}
